import Vue from 'vue'
import { DataTableHeader } from 'vuetify'
import { AxiosError, AxiosRequestConfig, AxiosRequestHeaders, RawAxiosRequestHeaders } from 'axios'

export enum BaseSearchComparison {
  EQUAL = 'EQUAL',
  NOT_EQUAL = 'NOT_EQUAL',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL = 'GREATER_THAN_OR_EQUAL',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_OR_EQUAL = 'LESS_THAN_OR_EQUAL',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  STARTS_WITH = 'STARTS_WITH',
  NOT_STARTS_WITH = 'NOT_STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  NOT_ENDS_WITH = 'NOT_ENDS_WITH',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS'
}

export enum BaseSearchOperator {
  AND = 'AND',
  OR = 'OR'
}

export enum BaseAccessLevel {
  GUEST = 'GUEST',
  OPERATOR = 'OPERATOR',
  MANAGER = 'MANAGER',
  ADMINISTRATOR = 'ADMINISTRATOR'
}

export enum BaseFieldConstraint {
  NOT_NULL = 'NOT_NULL',
  NOT_INSERT = 'NOT_INSERT',
  NOT_UPDATE = 'NOT_UPDATE'
}

export type BaseEnumValue = string | number

export interface BaseEnum<T extends BaseEnumValue> {
  [key: string]: T | string
  [key: number]: string
}

export interface BaseDatetimeFormatOptions {
  pattern: string
  timeZone?: string
}

export interface BasePredicate<T> {
  (value: T, valueIndex: number, values: T[]): boolean
}

export interface BaseCallback<T> {
  (value: T): void
}

export interface BaseHook<T> {
  (value: T): void | Promise<void>
}

export interface BaseHooks<T> {
  register(hook: BaseHook<T>): void
  execute(value: T): void
}

export interface BaseModuleConfig<T> {
  moduleName: string
  moduleValue: T
}

export interface BaseUrlVariables {
  [key: string]: string
}

export interface BaseRequestConfig<T = any> extends AxiosRequestConfig<T> {
  headers?: RawAxiosRequestHeaders
  authorized?: boolean
  urlVariables?: BaseUrlVariables
  fallbackStorageKey?: string
  fallbackDataCallback?: BaseCallback<T>
}

export interface BaseRequestOptions<T> {
  requestPath: string
  requestConfig?: BaseRequestConfig<T>
}

export interface BaseErrorChain {
  errorName: string
  errorMessage: string
  fieldName?: string
}

export interface BaseError<R = any> extends AxiosError<BaseErrorChain[], R> {
  config: BaseRequestConfig<R> & { headers: AxiosRequestHeaders }
}

export interface BaseSearchComparisons {
  [key: string | BaseSearchComparison]: any
}

export interface BaseSearchConditions {
  [key: string]: BaseSearchComparisons
}

export interface BaseSearch {
  conditions: BaseSearchConditions
  operator?: BaseSearchOperator
}

export interface BaseEntity {
  id: any
  [key: string]: any | undefined
}

export interface BaseSort {
  fieldName: string
  fieldDesc: boolean
}

export interface BasePage<T> {
  content: T[]
  sort: BaseSort[]
  size: number
  page: number
  currentSize: number
  totalSize: number
  pages: number
  first: boolean
  last: boolean
}

export interface BaseAutocomplete<T> {
  value: T
  text: string
}

export interface BaseTableHeader<T = any> extends DataTableHeader<T> {
  index?: number
  type?: string
  hidden?: boolean | { [key: string]: boolean }
  multiple?: boolean
  chips?: boolean
  auditing?: boolean | { [key: string]: boolean }
  required?: boolean
  searchPath?: string
  subnames?: string[]
  enums?: string[]
  style?: string
  pattern?: string
  timeZone?: string
  currency?: string
  unit?: string
  minimumIntegerDigits?: number
  minimumFractionDigits?: number
  isDisabled?(inserting: boolean): boolean
  formatter?(value: T, rootValue: any): any
  prependIcon?(value: T, rootValue: any): string
  appendIcon?(value: T, rootValue: any): string
}

export interface BaseTableEntities {
  [key: string]: BaseTableHeader[] | undefined
}

export interface BaseTableModules {
  [key: string]: BaseTableEntities | undefined
}

export interface BaseServerRequestGetPage {
  execute<R>(requestConfig?: BaseRequestConfig<BasePage<R>>): Promise<BasePage<R>>
  checkAccess(): boolean
  defaultSize?: number
  defaultPage?: number
}

export interface BaseServerRequestGet {
  execute<R>(requestConfig?: BaseRequestConfig<R>): Promise<R>
  checkAccess(): boolean
}

export interface BaseServerRequestPost {
  execute<R>(requestData?: any, requestConfig?: BaseRequestConfig<R>): Promise<R>
  checkAccess(): boolean
}

export interface BaseServerRequestPut {
  execute<R>(requestData?: any, requestConfig?: BaseRequestConfig<R>): Promise<R>
  checkAccess(): boolean
}

export interface BaseServerRequestDelete {
  execute<R>(requestConfig?: BaseRequestConfig<R>): Promise<R>
  checkAccess(): boolean
}

export interface BaseServerRequestMethods {
  getPage?: BaseServerRequestGetPage
  get?: BaseServerRequestGet
  post?: BaseServerRequestPost
  put?: BaseServerRequestPut
  delete?: BaseServerRequestDelete
}

export interface BaseServerRequests {
  [key: string]: BaseServerRequestMethods | undefined
}

export interface BaseServerControllers {
  [key: string]: BaseServerRequests | undefined
}

export interface BaseServerModules {
  [key: string]: BaseServerControllers | undefined
}

export interface BaseForm extends Vue {
  inputs: any[] //TODO: verificar o tipo correto
  validate(): boolean
  resetValidation(): void
}

export interface BaseField {
  name: string
  type?: string
  multiple?: boolean
  transient?: boolean
  auditing?: boolean
  constraints?: BaseFieldConstraint[]
  searchPath?: string
  subnames?: string[]
  enums?: string[]
  style?: string
  pattern?: string
  timeZone?: string
  currency?: string
  unit?: string
  minimumIntegerDigits?: number
  minimumFractionDigits?: number
}

export interface BaseNotificationClient {
  cloudPlatform: string
  token: string
}

export interface BaseNotification {
  id?: number
  title: string
  icon?: string
  clickAction?: string
  clickTitle?: string
  uniqueKey?: string
  temporary?: boolean
  autoremove?: boolean
  datetime?: string
  color?: string
}

export interface BaseRequestMethod {
  methodName: string
  accessLevel?: BaseAccessLevel
  defaultSize?: number
  defaultPage?: number
}

export interface BaseRequest {
  subpath: string
  methods: BaseRequestMethod[]
}

export interface BaseController {
  controllerName: string
  entityName: string
  requestName: string
  requestPath: string
  requestDefaultSubpath: string
  requests: BaseRequest[]
  fields: BaseField[]
  title?: string
  routePath?: string
}

export interface BaseManifest {
  moduleName: string
  moduleBarColor: string
  modulePrimaryColor: string
  moduleClientUrl?: string
  controllers: BaseController[]
  title?: string
  routePath?: string
}

export interface BaseAccess {
  moduleName: string
  accessId: string
  accessLevel: BaseAccessLevel
  groupName: string
  groupReferences: string[]
}

export interface BaseUserDetails {
  name?: string
  email?: string
  accesses?: BaseAccess[]
  manifests?: BaseManifest[]
}

export interface BaseToken {
  accessToken?: string
  refreshToken?: string
  userDetails?: BaseUserDetails
}

export interface BaseUserModule {
  moduleName: string
  moduleBarColor: string
  modulePrimaryColor: string
  moduleClientUrl?: string
  controllers: BaseController[]
  title: string
  routePath: string
  accessId: string
  accessLevel: BaseAccessLevel
  groupName: string
  groupReferences: string[]
}